import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { 
  BarChart3, TrendingUp, TrendingDown, Users, Clock, QrCode,
  AlertTriangle, Calendar, Download, Loader2, MapPin, CheckCircle,
  Target, Award, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { api } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useToast } from '../../hooks/use-toast';

export default function AdminAnalytics() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [stats, setStats] = useState(null);
  const [complianceData, setComplianceData] = useState(null);
  const [recentActivity, setRecentActivity] = useState({ scans: [], incidents: [] });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsData, complianceResponse, scansData, incidentsData] = await Promise.all([
        api.getDashboardStats(),
        api.getShiftCompliance(period),
        api.getScans({ limit: 10 }),
        api.getIncidents(),
      ]);
      
      setStats(statsData);
      setComplianceData(complianceResponse);
      setRecentActivity({
        scans: scansData,
        incidents: incidentsData.slice(0, 5),
      });
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExportReport = async () => {
    try {
      const endDate = format(new Date(), 'yyyy-MM-dd');
      let startDate;
      
      if (period === 'week') {
        startDate = format(subDays(new Date(), 7), 'yyyy-MM-dd');
      } else if (period === 'month') {
        startDate = format(subDays(new Date(), 30), 'yyyy-MM-dd');
      } else {
        startDate = format(subDays(new Date(), 90), 'yyyy-MM-dd');
      }
      
      const data = await api.exportTimesheetCSV(startDate, endDate);
      
      const blob = new Blob([data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics_report_${startDate}_to_${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Report Exported",
        description: "Analytics report downloaded successfully",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Export Failed",
        description: error.message || 'Failed to export report',
      });
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
        </div>
      </AdminLayout>
    );
  }

  const complianceRate = complianceData?.compliance_rate || 0;
  const onTimeRate = complianceData?.on_time_rate || 0;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-analytics-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Analytics & Reports</h1>
            <p className="text-slate-500">Performance metrics and insights</p>
          </div>
          <div className="flex gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">This Week</SelectItem>
                <SelectItem value="month">This Month</SelectItem>
                <SelectItem value="quarter">This Quarter</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleExportReport} className="bg-green-600 hover:bg-green-700 gap-2">
              <Download className="w-4 h-4" />
              Export Report
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Shift Compliance"
            value={`${complianceRate.toFixed(0)}%`}
            subtitle={`${complianceData?.total_shifts || 0} total shifts`}
            icon={Target}
            color="blue"
            trend={complianceRate >= 90 ? 'up' : complianceRate >= 75 ? 'neutral' : 'down'}
          />
          <KPICard
            title="On-Time Rate"
            value={`${onTimeRate.toFixed(0)}%`}
            subtitle={`${complianceData?.late_checkins || 0} late check-ins`}
            icon={Clock}
            color={onTimeRate >= 90 ? 'green' : onTimeRate >= 75 ? 'amber' : 'red'}
            trend={onTimeRate >= 90 ? 'up' : 'down'}
          />
          <KPICard
            title="Total Scans"
            value={stats?.scans_today || 0}
            subtitle="Today"
            icon={QrCode}
            color="sky"
            trend="neutral"
          />
          <KPICard
            title="Open Incidents"
            value={stats?.open_incidents || 0}
            subtitle="Requiring attention"
            icon={AlertTriangle}
            color={stats?.open_incidents > 0 ? 'amber' : 'green'}
            trend={stats?.open_incidents > 0 ? 'down' : 'up'}
          />
        </div>

        {/* Compliance Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Shift Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Shift Performance</CardTitle>
              <CardDescription>Breakdown of shift attendance</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <ProgressBar 
                  label="Completed Shifts" 
                  value={complianceData?.completed_shifts || 0}
                  total={complianceData?.total_shifts || 1}
                  color="green"
                />
                <ProgressBar 
                  label="In Progress" 
                  value={complianceData?.in_progress_shifts || 0}
                  total={complianceData?.total_shifts || 1}
                  color="blue"
                />
                <ProgressBar 
                  label="Missed Shifts" 
                  value={complianceData?.missed_shifts || 0}
                  total={complianceData?.total_shifts || 1}
                  color="red"
                />
              </div>
              
              <div className="pt-4 border-t">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-green-600">{complianceData?.completed_shifts || 0}</p>
                    <p className="text-xs text-slate-500">Completed</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{complianceData?.in_progress_shifts || 0}</p>
                    <p className="text-xs text-slate-500">In Progress</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">{complianceData?.missed_shifts || 0}</p>
                    <p className="text-xs text-slate-500">Missed</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Performers */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                Top Performers
              </CardTitle>
              <CardDescription>Guards with best compliance this {period}</CardDescription>
            </CardHeader>
            <CardContent>
              {complianceData?.top_performers?.length > 0 ? (
                <div className="space-y-3">
                  {complianceData.top_performers.slice(0, 5).map((performer, idx) => (
                    <div key={performer.guard_id} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                        idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-700' : 'bg-slate-300'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{performer.guard_name}</p>
                        <p className="text-xs text-slate-500">{performer.shifts_completed} shifts completed</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-600">{performer.compliance_rate?.toFixed(0)}%</p>
                        <p className="text-xs text-slate-500">compliance</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No data available for this period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Scans */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Checkpoint Scans</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentActivity.scans.length === 0 ? (
                  <p className="text-center py-4 text-slate-500">No recent scans</p>
                ) : (
                  recentActivity.scans.slice(0, 5).map((scan) => (
                    <div key={scan.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div className="flex-1">
                        <p className="font-medium text-slate-800 text-sm">{scan.checkpoint_name}</p>
                        <p className="text-xs text-slate-500">{scan.guard_name} • {scan.site_name}</p>
                      </div>
                      <span className="text-xs text-slate-500">
                        {format(new Date(scan.scanned_at), 'h:mm a')}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Incidents */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Incidents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentActivity.incidents.length === 0 ? (
                  <p className="text-center py-4 text-slate-500">No recent incidents</p>
                ) : (
                  recentActivity.incidents.map((incident) => (
                    <div key={incident.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                      <AlertTriangle className={`w-5 h-5 ${
                        incident.severity === 'critical' ? 'text-red-600' :
                        incident.severity === 'high' ? 'text-orange-600' :
                        incident.severity === 'medium' ? 'text-amber-600' : 'text-green-600'
                      }`} />
                      <div className="flex-1">
                        <p className="font-medium text-slate-800 text-sm">{incident.title}</p>
                        <p className="text-xs text-slate-500">{incident.reporter_name}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        incident.status === 'open' ? 'bg-amber-100 text-amber-700' :
                        incident.status === 'investigating' ? 'bg-blue-100 text-blue-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {incident.status}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Organization Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <Users className="w-6 h-6 mx-auto text-blue-700 mb-2" />
                <p className="text-2xl font-bold text-slate-800">{stats?.total_guards || 0}</p>
                <p className="text-xs text-slate-500">Total Guards</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <MapPin className="w-6 h-6 mx-auto text-green-700 mb-2" />
                <p className="text-2xl font-bold text-slate-800">{stats?.total_sites || 0}</p>
                <p className="text-xs text-slate-500">Active Sites</p>
              </div>
              <div className="text-center p-4 bg-sky-50 rounded-lg">
                <QrCode className="w-6 h-6 mx-auto text-sky-700 mb-2" />
                <p className="text-2xl font-bold text-slate-800">{stats?.total_checkpoints || 0}</p>
                <p className="text-xs text-slate-500">Checkpoints</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <Clock className="w-6 h-6 mx-auto text-purple-700 mb-2" />
                <p className="text-2xl font-bold text-slate-800">{stats?.active_today || 0}</p>
                <p className="text-xs text-slate-500">Active Today</p>
              </div>
              <div className="text-center p-4 bg-amber-50 rounded-lg">
                <Calendar className="w-6 h-6 mx-auto text-amber-600 mb-2" />
                <p className="text-2xl font-bold text-slate-800">{complianceData?.total_hours?.toFixed(0) || 0}</p>
                <p className="text-xs text-slate-500">Hours This {period}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

function KPICard({ title, value, subtitle, icon: Icon, color, trend }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    sky: 'bg-sky-50 text-sky-700',
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          {trend === 'up' && <ArrowUpRight className="w-5 h-5 text-green-500" />}
          {trend === 'down' && <ArrowDownRight className="w-5 h-5 text-red-500" />}
        </div>
        <div className="mt-3">
          <p className="text-2xl font-bold text-slate-800">{value}</p>
          <p className="text-sm text-slate-600">{title}</p>
          <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function ProgressBar({ label, value, total, color }) {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  const colorClasses = {
    green: 'bg-green-500',
    blue: 'bg-blue-500',
    red: 'bg-red-500',
    amber: 'bg-amber-500',
  };

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-slate-600">{label}</span>
        <span className="font-medium text-slate-800">{value} ({percentage.toFixed(0)}%)</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${colorClasses[color]} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}
